import * as React from "react";
import * as yup from "yup";

import {
  CompanyDto,
  DataImage,
  DocumentDto,
  getCompaniesGetAllIdentificationNumbers,
} from "Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { cloneDeep, isEqual } from "lodash";
import { CompanyEditBasicInfo } from "Components/Companies/CompanyEdit/CompanyEditBasicInfo/CompanyEditBasicInfo";
import { CompanyEditCheckboxes } from "Components/Companies/CompanyEdit/CompanyEditCheckboxes";
import { CompanyEditImage } from "Components/Companies/CompanyEdit/CompanyEditImage";
import { EditDocuments } from "Components/Shared/Documents/EditDocuments";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { nameof } from "Utils/ObjectUtils";
import { setDisableRedirect } from "State/App/AppReducer";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import useAutoAdder from "Hooks/useAutoAdder";

type Props = {
  company: CompanyDto;
  isLoading: boolean;
  created: boolean;
  onSave: (companyToSave: CompanyDto) => void;
};

type FormData = Pick<
  CompanyDto,
  | "name"
  | "identificationNumber"
  | "taxIdentificationNumber"
  | "street"
  | "streetNo"
  | "postalCode"
  | "city"
  | "documents"
  | "isCustomer"
  | "isSupplier"
  | "signaturePhotoUrl"
  | "image"
  | "cooperationDueDate"
  | "isMarketplaceEnabled"
  | "logoPhotoUrl"
  | "logo"
>;
export type CompanyFormData = FormData;

const MAX_SIZE = 4 * 1024 * 1024;

const SUPPORTED_FORMATS = [".png"];

export { MAX_SIZE, SUPPORTED_FORMATS };

export const CompanyEditForm: React.FunctionComponent<Props> = props => {
  const { company, created, onSave } = props;
  const { t } = useResource();
  const dispatch = useDispatch();
  const [identificationNumbers, setIdentificationNumbers] = React.useState(
    [] as string[],
  );

  const requiredString = (validationMessage: string) =>
    yup.string().defined().required(validationMessage);

  const identificationNumberValidationSchema = () => {
    return yup
      .string()
      .matches(/^[0-9]*$/, {
        message: t(Resources.CompanyEdit.IdentificationNumber.Validation.Regex),
        excludeEmptyString: true,
      })
      .defined()
      .max(10, t(Resources.CompanyEdit.IdentificationNumber.Validation.Max))
      .required(
        t(Resources.CompanyEdit.IdentificationNumber.Validation.Required),
      );
  };

  const isExistIdentificationNumber = (identificationNumber: string) =>
    identificationNumber && identificationNumber !== "0" && identificationNumbers.includes(identificationNumber)
      ? true
      : false;

  React.useEffect(() => {
    getCompaniesGetAllIdentificationNumbers().then(result => {
      setIdentificationNumbers(result.json);
    });
  }, []);

  const isValidIdentificationNumber = (identificationNumber: string) => {
    let isValid = true;
    if (
      identificationNumber &&
      company.identificationNumber !== identificationNumber &&
      isExistIdentificationNumber(identificationNumber)
    ) {
      isValid = false;
    }
    return isValid;
  };

  const isValidSize = (image: DataImage) => {
    let isValid = true;
    if (image && image.size && image.size > MAX_SIZE) {
      isValid = false;
    }
    return isValid;
  };

  const isValidFileExtension = (image: DataImage) => {
    let isValid = true;
    if (image && image.name) {
      const result = SUPPORTED_FORMATS.filter(value =>
        image.name.toLowerCase().includes(value),
      );
      isValid = result.length !== 0;
    }
    return isValid;
  };

  const isValidImage = yup
    .object<DataImage>()
    .test(
      "validFileExtension",
      t(Resources.CompanyEdit.Image.Validation.FileExtension),
      value => isValidFileExtension(value),
    )
    .test(
      "validSize",
      t(Resources.CompanyEdit.Image.Validation.MaxSize),
      value => isValidSize(value),
    );

  const validationSchema: yup.ObjectSchema<FormData> = yup
    .object({
      name: requiredString(t(Resources.CompanyEdit.Name.Validation)),
      identificationNumber: identificationNumberValidationSchema(),
      taxIdentificationNumber: yup
        .string()
        .max(
          16,
          t(Resources.CompanyEdit.TaxIdentificationNumber.Validation.Max),
        ),
      street: requiredString(t(Resources.CompanyEdit.Street.Validation)),
      streetNo: requiredString(t(Resources.CompanyEdit.StreetNo.Validation)),
      postalCode: requiredString(
        t(Resources.CompanyEdit.PostalCode.Validation),
      ),
      city: requiredString(t(Resources.CompanyEdit.City.Validation)),
      documents: yup.array<DocumentDto>().defined(),
      isCustomer: yup.boolean().defined(),
      isSupplier: yup.boolean().defined(),
      signaturePhotoUrl: yup.string(),
      logoPhotoUrl: yup.string(),
      isMarketplaceEnabled: yup.boolean().defined(),
      cooperationDueDate: yup
        .string()
        .optional()
        .transform((value, originalValue) =>
          originalValue === null || String(originalValue).trim() === ""
            ? undefined
            : value,
        ),
      logo: isValidImage,
      image: isValidImage,
    })
    .defined();

  const getDefaultValues = React.useCallback(
    () => ({
      name: company.name ?? "",
      identificationNumber: company.identificationNumber ?? "",
      taxIdentificationNumber: company.taxIdentificationNumber ?? "",
      street: company.street ?? "",
      streetNo: company.streetNo ?? "",
      postalCode: company.postalCode ?? "",
      city: company.city ?? "",
      isSupplier: company.isSupplier ?? false,
      isCustomer: company.isCustomer ?? true,
      documents: cloneDeep(company.documents) ?? [],
      signaturePhotoUrl: company.signaturePhotoUrl ?? undefined,
      logoPhotoUrl: company.logoPhotoUrl ?? undefined,
      image: company.image ?? undefined,
      isMarketplaceEnabled: company.isMarketplaceEnabled ?? false,
      cooperationDueDate: company.cooperationDueDate,
      logo: undefined,
    }),
    [company],
  );

  const form = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: getDefaultValues(),
  });
  const { reset, watch } = form;
  const { ...formValues } = watch();

  React.useEffect(() => {
    reset(getDefaultValues());
  }, [company, getDefaultValues, reset]);

  React.useEffect(() => {
    if (!created) {
      dispatch(setDisableRedirect(!isEqual(formValues, getDefaultValues())));
    }
  }, [dispatch, formValues, getDefaultValues, created]);

  const onSubmit = (data: FormData) => {
    if (!isValidIdentificationNumber(data.identificationNumber)) {
      form.setError(nameof<CompanyFormData>("identificationNumber"), {
        message: t(
          Resources.CompanyEdit.IdentificationNumber.Validation.Equals,
        ),
        type: "manual",
      });
      window.scrollTo(0, 0);
      return;
    }

    if (!autoAdder.checkAutoAdd()) {
      return;
    }

    const companyToSave = {
      ...company,
      ...data,
    };
    onSave(companyToSave);
  };

  const autoAdder = useAutoAdder();

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <CompanyEditBasicInfo form={form} />

      <CompanyEditCheckboxes form={form} />

      <CompanyEditImage
        form={form}
        imageField={"image"}
        photoUrlField={"signaturePhotoUrl"}
        label={t(Resources.CompanyEdit.Image.Label)}
      />

      <CompanyEditImage
        form={form}
        imageField={"logo"}
        photoUrlField={"logoPhotoUrl"}
        label="Logo"
      />

      <EditDocuments
        title={t(Resources.CompanyEdit.Documents.Label)}
        isCompany={true}
        autoAdderRegister={autoAdder.registerOperation}
        form={form}
      />

      <PrimaryButton type={"submit"}>
        {t(Resources.CompanyEdit.Save)}
      </PrimaryButton>
    </form>
  );
};
